/**
 * TekInvest — Strategy Adjustment Engine.
 *
 * Lets an experienced trader manually modify a recommendation the
 * (untouched) Strategy Recommendation engine already produced — change
 * strikes, expiry, quantity, order type, hedges, or convert the whole
 * strategy shape — before anything ever reaches a broker. Reuses the
 * existing Strategy Recommendation API (`strategyAdjustmentService`
 * itself only reads `/strategy-adjustment/*`, which in turn reuses
 * `StrategyRecommendationService.get_current()` — see backend
 * `strategy_adjustment_service.py`) and the existing Broker Connection APIs
 * for the connected-broker dropdown inside `ExecutionPanel`. Follows the
 * existing TekInvest UI theme (MUI, same Card/Grid/Typography conventions
 * as `StrategyRecommendationSection.tsx`).
 *
 * EXECUTION SAFETY: live order placement IS implemented and connected —
 * `ExecutionPanel` below (`app.api.v1.execution`, Phase 4) genuinely sends
 * orders to the trader's selected, connected broker. The safeguard is not
 * "nothing can be placed" but "there is exactly one, explicit, final gate
 * before anything is": a connected broker must be selected, a fresh
 * Execution Preview must be reviewed, the single confirmation checkbox
 * ("I confirm that I have reviewed the strategy, quantities, prices and
 * estimated risk.") must be checked, and even then the backend still runs
 * its own `TRADING_MODE=LIVE` gate, idempotency check, and a FRESH final
 * broker validation before any order reaches the broker — see
 * `ExecutionPanel.tsx`'s own docstring. Earlier drafts of this tab had a
 * second, disconnected "User Confirmation" card left over from before live
 * execution existed; it and the old display-only, multi-broker "Execution
 * Preview" card were removed as redundant/contradictory — this tab now has
 * exactly one execution preview and one final confirmation, both inside
 * `ExecutionPanel`. Every RECALCULATION still only happens in response to
 * the trader's own edits — `legs`, `metrics`, and `original` are never
 * silently replaced by anything running in the background.
 *
 * Live signal drift banner (Option Analyst audit backlog item): a single
 * silent, read-only, market-hours-agnostic 60s poll (mirroring the
 * existing `silent: true` background job-poll pattern already used
 * elsewhere in this app, e.g. `OIExportSection.tsx`) re-fetches the
 * CURRENT recommendation via the same `strategyRecommendationService.
 * getCurrent()` the AI Insights tab already calls, and compares it
 * against the strategy this tab opened with. If the underlying market
 * snapshot has gone stale, its data-quality floor no longer holds, or the
 * Best Match strategy itself has changed, an `Alert` banner tells the
 * trader — it never touches `legs`/`metrics`/`original` itself, and the
 * trader must explicitly go re-check AI Insights to see updated numbers.
 * This is deliberately the same "detect drift, inform, let the trader
 * act" shape `SimulationCard.tsx` already uses for its own stale-
 * simulation banner, not an auto-refresh of anything feeding execution.
 *
 * Embedding: this component is now rendered as the "Strategy Builder" tab
 * (formerly "Adjust Strategy") inside `OptionsDashboardPage` (the unified
 * Options Analytics / AI Insights / Strategy Builder workflow), which
 * already tracks the selected symbol itself — passed in here as
 * `symbolProp` so the tab shares that same symbol instead of re-deriving
 * it from a route param. The `:symbol` route param fallback is kept so
 * this component still works if rendered directly at a URL during
 * development/testing.
 * `onViewRecommendation` is likewise supplied by that parent tab — it
 * switches to the "AI Insights" tab (formerly "AI Strategy
 * Recommendation"), since that's now the ONLY place the full
 * recommendation (confidence, OI Score, spot, trend/regime, market
 * thesis, generated legs) is shown — see `SelectedStrategyBar.tsx`'s own
 * docstring for why this page doesn't repeat any of that.
 *
 * Scope: Strategy Builder -> Risk/Greeks -> Simulate -> Execution, nothing
 * else. This tab starts from a strategy the AI Insights tab already
 * produced and ends at the existing Execution/Order workflow — it is
 * deliberately not a second place to read the recommendation itself.
 * `SimulationCard` ("Simulate Strategy") is a read-only, on-demand payoff
 * table for the current legs — it calls a dedicated `/simulate` endpoint
 * that reuses `recalculate()`'s own pricing logic, places no order, and
 * creates no paper position; "Proceed to Execution" just scrolls to the
 * existing `ExecutionPanel` below (`executionRef`), it doesn't add a new
 * workflow.
 *
 * Layout: deliberately has NO `Container`/max-width wrapper and no
 * page-level title block of its own — it renders its card `Stack`
 * directly, the same way the "Options Analytics" and "AI Insights" tabs
 * do, so it fills the same content width and sits at the same horizontal
 * position as the other two tabs instead of reading as a separate
 * standalone page nested inside one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Card, CardContent, CircularProgress, Link as MuiLink, Stack, Typography } from "@mui/material";
import strategyAdjustmentService from "@/services/strategyAdjustmentService";
import strategyRecommendationService from "@/services/strategyRecommendationService";
import type {
  AdjustableStrategyType,
  OptionLeg,
  OriginalRecommendationRead,
  PayoffCurveRead,
  RecalculatedMetricsRead,
  ValidationResultRead,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import {
  PayoffChart,
  RecalculatedMetricsCard,
  SelectedStrategyBar,
  SimulationCard,
  TradeBuilderCard,
  mapRecommendationKeyToAdjustableType,
} from "@/pages/strategy-adjustment";
import { ExecutionPanel } from "@/pages/execution";

const RECALC_DEBOUNCE_MS = 450;
// Live signal drift banner poll interval — same 60s cadence the OI
// dashboard's own auto-refresh pages already use (e.g. `OIChartsPage.tsx`,
// `DualSymbolDashboardPage.tsx`), reused here for consistency rather than
// a new interval invented for this one tab.
const DRIFT_POLL_MS = 60_000;

interface MarketDrift {
  dataStale: boolean;
  qualityDegraded: boolean;
  bestKeyChanged: string | null; // the new Best Match display name, or null if unchanged
}

function addDaysToIsoDate(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function StrategyAdjustmentPage({
  symbolProp,
  onViewRecommendation,
}: {
  symbolProp?: string;
  onViewRecommendation?: () => void;
} = {}) {
  const { symbol: routeSymbol = "" } = useParams<{ symbol: string }>();
  const symbol = symbolProp ?? routeSymbol;

  const [original, setOriginal] = useState<OriginalRecommendationRead | null>(null);
  const [strategyType, setStrategyType] = useState<AdjustableStrategyType | null>(null);
  const [legs, setLegs] = useState<OptionLeg[]>([]);

  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);
  const [convertBusy, setConvertBusy] = useState(false);

  const [metrics, setMetrics] = useState<RecalculatedMetricsRead | null>(null);
  // Payoff Visualizer (Iron Condor Strike Manipulation Engine) — fetched by
  // the SAME debounced effect as `metrics`/`validation` below, so the T+0
  // curve/backend metrics always refresh together for one consistent
  // `legs` snapshot. `isDraggingChart` (set by `PayoffChart`'s
  // `onDragActiveChange`) suppresses this effect's network calls entirely
  // for the duration of a strike-handle drag — see that component's own
  // "DEBOUNCE CONTRACT" docstring for why: the chart's solid Expiry P&L
  // line already redraws live, with zero backend round-trip, from `legs`
  // alone, so nothing is lost by not recalculating the (genuinely
  // model-dependent) T+0 line/margin/Greeks until the mouse is released.
  const [payoffCurve, setPayoffCurve] = useState<PayoffCurveRead | null>(null);
  const [isDraggingChart, setIsDraggingChart] = useState(false);
  // Nothing in this tab's UI reads `validation` directly anymore — its
  // former consumers (a compact Execution readiness card, and the old
  // "User Confirmation" card) were both removed as redundant with what
  // `ExecutionPanel` below already shows and re-derives itself (a fresh,
  // live broker validation runs there immediately before placement). The
  // `strategyAdjustmentService.validate()` call and state update still run
  // unchanged in case a future card needs this same read-only check.
  const [, setValidation] = useState<ValidationResultRead | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  // Risk / Reward / Greeks' "Underlying price" editable what-if input
  // (Phase 2) — null means "use the recommendation's own live spot."
  const [underlyingPriceOverride, setUnderlyingPriceOverride] = useState<number | null>(null);
  // Live signal drift banner — see module docstring. `null` = no drift
  // detected (or not checked yet); never influences `legs`/`metrics`.
  const [marketDrift, setMarketDrift] = useState<MarketDrift | null>(null);
  const [driftDismissed, setDriftDismissed] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Simulate Strategy's "Proceed to Execution" scrolls down to the existing
  // Execution section below rather than navigating anywhere new — this tab
  // has exactly one execution workflow, already rendered by `ExecutionPanel`.
  const executionRef = useRef<HTMLDivElement | null>(null);
  const handleProceedToExecution = useCallback(() => {
    executionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const nearExpiryDate = original?.expiry_date ?? "";
  const farExpiryDate = useMemo(() => {
    const farLeg = legs.find((leg) => leg.expiry_bucket === "FAR");
    if (farLeg) return farLeg.expiry_date;
    return nearExpiryDate ? addDaysToIsoDate(nearExpiryDate, 7) : "";
  }, [legs, nearExpiryDate]);

  // Initial load: the original recommendation, then the Trade Builder's
  // starting legs synthesized from that same strategy shape.
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setInitialLoading(true);
    setInitialError(null);

    (async () => {
      try {
        const rec = await strategyAdjustmentService.getOriginal(symbol);
        if (cancelled) return;
        setOriginal(rec);

        const mapped = mapRecommendationKeyToAdjustableType(rec.strategy_key);
        setStrategyType(mapped);
        if (mapped) {
          const synthesized = await strategyAdjustmentService.synthesize(symbol, mapped);
          if (!cancelled) setLegs(synthesized.legs);
        } else {
          setLegs([]);
        }
      } catch (err) {
        if (!cancelled) setInitialError(extractErrorMessage(err));
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [symbol]);

  // Live signal drift banner (see module docstring) — starts only once the
  // original recommendation has loaded, resets on symbol change. Silent:
  // never sets `initialLoading`/`initialError`/`refreshLoading`, and never
  // touches `legs`/`metrics`/`original` — purely an awareness check.
  useEffect(() => {
    if (!symbol || !original) {
      setMarketDrift(null);
      setDriftDismissed(false);
      return;
    }
    let cancelled = false;

    const checkDrift = async () => {
      try {
        const current = await strategyRecommendationService.getCurrent(symbol);
        if (cancelled) return;
        const bestKeyChanged =
          current.best_match && current.best_match.key !== original.strategy_key
            ? current.best_match.display_name
            : null;
        const qualityDegraded = current.best_match?.data_quality_ok === false;
        const dataStale = current.data_stale === true;
        if (dataStale || qualityDegraded || bestKeyChanged) {
          setMarketDrift((prev) => {
            const next = { dataStale, qualityDegraded, bestKeyChanged };
            // A NEW drift signature (not just the same one still true on
            // the next poll) re-surfaces the banner even if the trader
            // dismissed an earlier one.
            const changed =
              !prev ||
              prev.dataStale !== next.dataStale ||
              prev.qualityDegraded !== next.qualityDegraded ||
              prev.bestKeyChanged !== next.bestKeyChanged;
            if (changed) setDriftDismissed(false);
            return next;
          });
        } else {
          setMarketDrift(null);
        }
      } catch {
        // Silent by design — a failed background check is not worth
        // surfacing as an error on top of the trader's own edits.
      }
    };

    const intervalId = window.setInterval(checkDrift, DRIFT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [symbol, original]);

  // Risk/Greeks — recomputed from scratch every time the trader changes
  // anything in the Trade Builder, debounced so fast edits (e.g. typing a
  // strike) don't fire a call per keystroke. The old display-only
  // "Execution Preview" (all 3 brokers, none connected) and "User
  // Confirmation" cards used to be refreshed here too; both were removed as
  // redundant with `ExecutionPanel`'s own, single, connected-broker preview
  // and confirmation below, so this effect no longer fetches their data.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!symbol || !original || legs.length === 0) {
      setMetrics(null);
      setValidation(null);
      setPayoffCurve(null);
      return;
    }

    // Suppressed for the duration of an active strike-handle drag on the
    // Payoff Visualizer — see `payoffCurve`'s own comment above. The moment
    // `isDraggingChart` flips back to `false` this effect re-runs (it's a
    // dependency below) and schedules the normal debounced refresh, i.e.
    // "full re-render on mouse release."
    if (isDraggingChart) return;

    debounceRef.current = setTimeout(() => {
      setRefreshLoading(true);
      setRefreshError(null);
      Promise.all([
        strategyAdjustmentService.recalculate(symbol, legs, underlyingPriceOverride),
        strategyAdjustmentService.validate(symbol, legs, undefined, underlyingPriceOverride),
        strategyAdjustmentService.payoffCurve(symbol, legs, underlyingPriceOverride),
      ])
        .then(([metricsResult, validationResult, payoffCurveResult]) => {
          setMetrics(metricsResult);
          setValidation(validationResult);
          setPayoffCurve(payoffCurveResult);
        })
        .catch((err) => setRefreshError(extractErrorMessage(err)))
        .finally(() => setRefreshLoading(false));
    }, RECALC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [symbol, legs, original, underlyingPriceOverride, isDraggingChart]);

  const handleConvertStrategy = useCallback(
    async (type: AdjustableStrategyType) => {
      if (!symbol) return;
      setConvertBusy(true);
      setInitialError(null);
      try {
        const synthesized = await strategyAdjustmentService.synthesize(symbol, type);
        setStrategyType(type);
        setLegs(synthesized.legs);
        // Converting is a WHOLESALE leg replacement (a Calendar Spread's
        // breakevens/Greeks/margin are not just "a bit stale" relative to
        // an Iron Condor's -- they're for a structurally different
        // position), unlike an incremental edit (typing a strike, dragging
        // a handle) where keeping the previous metrics on screen with a
        // refresh spinner while the debounced recalculate catches up is
        // intentional (see the debounced effect's own comment, and
        // `RecalculatedMetricsCard`/`SimulationCard`'s `isRefreshing`/
        // stale-banner handling). Left un-cleared here, `payoffCurve`
        // stayed OK-status from the OLD strategy for the ~450ms+ debounce
        // window, so `PayoffChart` rendered the NEW legs against the OLD
        // curve/metrics -- e.g. a Calendar Spread's breakeven feeding the
        // Payoff Visualizer's spot/breakeven safety net, which chased that
        // now-irrelevant value and blew the chart's x-axis out to ~30,000+
        // (reported bug). Clearing all three here means `PayoffChart`'s own
        // render guard (`payoffCurve && status === "OK"`) hides it for that
        // one brief window instead of ever rendering a legs/metrics
        // mismatch, then it reappears once the fresh recalculate lands.
        setMetrics(null);
        setPayoffCurve(null);
        setValidation(null);
      } catch (err) {
        setInitialError(extractErrorMessage(err));
      } finally {
        setConvertBusy(false);
      }
    },
    [symbol]
  );

  // Recommendation-divergence indicator + reset (review follow-up): the
  // AI's original pick, re-derived the same way `SelectedStrategyBar`
  // already does rather than a second source of truth -- `null` for
  // Covered Call/Protective Put, which aren't adjustable at all (see
  // `mapRecommendationKeyToAdjustableType`'s own docstring).
  const originalAdjustableType = useMemo(
    () => (original ? mapRecommendationKeyToAdjustableType(original.strategy_key) : null),
    [original]
  );
  const hasDivergedFromRecommendation = original !== null && strategyType !== originalAdjustableType;
  const handleResetToRecommended = useCallback(async () => {
    if (!symbol || !original) return;
    if (!originalAdjustableType) {
      // The original recommendation itself isn't adjustable -- this tab
      // opened with a blank builder, so "reset" means blank again, not a
      // guess at some other strategy shape.
      setStrategyType(null);
      setLegs([]);
      return;
    }
    await handleConvertStrategy(originalAdjustableType);
  }, [symbol, original, originalAdjustableType, handleConvertStrategy]);

  const defaultLotSize = legs[0]?.lot_size ?? 1;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Strategy Builder
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Modify {symbol}'s recommended strategy before execution. Nothing here changes the recommendation
        itself, and no order is ever placed automatically.
      </Typography>

      {initialError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {initialError}
        </Alert>
      )}

      {initialLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : original ? (
        <>
          {/* Live signal drift banner — see module docstring. Read-only
              awareness only; dismissing it does not affect the trade
              being built below. */}
          {marketDrift && !driftDismissed && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDriftDismissed(true)}>
              Market data has moved since this recommendation was generated —{" "}
              {marketDrift.bestKeyChanged
                ? `the current Best Match is now "${marketDrift.bestKeyChanged}". `
                : ""}
              {marketDrift.dataStale ? "The latest snapshot is stale. " : ""}
              {marketDrift.qualityDegraded ? "Data quality has dropped below the reliability floor. " : ""}
              {onViewRecommendation && (
                <MuiLink component="button" variant="body2" onClick={onViewRecommendation} sx={{ ml: 0.5 }}>
                  Review on AI Insights
                </MuiLink>
              )}
            </Alert>
          )}
          {/* Compact reference header, not a numbered workflow step — the
              full recommendation lives exclusively on the AI Insights tab
              (see `SelectedStrategyBar.tsx`). */}
          <Box sx={{ mb: 3 }}>
            <SelectedStrategyBar
              original={original}
              onViewRecommendation={onViewRecommendation}
              hasDiverged={hasDivergedFromRecommendation}
              onResetToRecommended={handleResetToRecommended}
              resetBusy={convertBusy}
            />
          </Box>

          <Stack spacing={3}>
            <TradeBuilderCard
              legs={legs}
              onChangeLegs={setLegs}
              nearExpiryDate={nearExpiryDate}
              farExpiryDate={farExpiryDate}
              defaultLotSize={defaultLotSize}
              strategyType={strategyType}
              onConvertStrategy={handleConvertStrategy}
              convertBusy={convertBusy}
              liveUnderlyingPrice={original.spot ?? null}
              underlyingPriceOverride={underlyingPriceOverride}
              onChangeUnderlyingPriceOverride={setUnderlyingPriceOverride}
            />

            {payoffCurve && payoffCurve.calculation_status === "OK" && (
              <Card variant="outlined">
                <CardContent>
                  <PayoffChart
                    curve={payoffCurve}
                    metrics={metrics}
                    legs={legs}
                    onChangeLegs={setLegs}
                    symbolCode={symbol}
                    liveUnderlyingPrice={original.spot ?? null}
                    underlyingPriceOverride={underlyingPriceOverride}
                    onChangeUnderlyingPriceOverride={setUnderlyingPriceOverride}
                    onDragActiveChange={setIsDraggingChart}
                  />
                </CardContent>
              </Card>
            )}

            <RecalculatedMetricsCard
              metrics={metrics}
              legs={legs}
              loading={refreshLoading && metrics === null}
              isRefreshing={refreshLoading && metrics !== null}
              error={refreshError}
            />

            <SimulationCard
              symbol={symbol}
              legs={legs}
              underlyingPriceOverride={underlyingPriceOverride}
              onProceedToExecution={handleProceedToExecution}
            />

            <Box ref={executionRef}>
              <ExecutionPanel symbol={symbol} originalStrategyKey={original.strategy_key} legs={legs} />
            </Box>
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No recommendation available yet for {symbol}.
        </Typography>
      )}
    </Box>
  );
}

export default StrategyAdjustmentPage;
